import type { DesignDocument, ImageLayer, Layer } from './document/types.ts'
import { MM_PER_INCH } from './units.ts'
import { visibleSourcePx, computeImagePlacement, placementCoversFrame } from './layout/imagePlacement.ts'

export type WarningCode =
  | 'LOW_DPI'
  | 'CONTENT_IN_BLEED'
  | 'TEXT_OUTSIDE_SAFE'
  | 'EMPTY_SLOT'
  | 'TEXT_OVERFLOW'
  | 'FONT_MISSING'
  | 'IMAGE_NOT_READY'
  | 'UNSUPPORTED_UPLOAD_FORMAT'
  | 'IMAGE_NOT_COVERING'
  | 'EMPTY_TEXT'

export type DesignWarning = {
  code: WarningCode
  severity: 'block' | 'warn'
  layerId?: string
  message: string
  data?: Record<string, unknown>
}

export type UploadInfo = { widthPx: number; heightPx: number; status?: string; mime?: string }

export type WarningsContext = {
  uploads?: Record<string, UploadInfo>
  /** Aktive, druckbare Font-Familien (familyId) */
  fonts?: Set<string> | string[]
  limits?: { minDpiWarn?: number; minDpiBlock?: number }
  /** Ergebnis der Client-Messung (dom/measureText): Layer-IDs mit Überlauf */
  overflowingTextLayerIds?: string[]
  unsupportedMimes?: string[]
}

function rotatedBounds(layer: Layer): { x1: number; y1: number; x2: number; y2: number } {
  const f = layer.frame
  const cx = f.xMm + f.wMm / 2
  const cy = f.yMm + f.hMm / 2
  const a = (f.rotationDeg * Math.PI) / 180
  const cos = Math.abs(Math.cos(a))
  const sin = Math.abs(Math.sin(a))
  const hw = (f.wMm * cos + f.hMm * sin) / 2
  const hh = (f.wMm * sin + f.hMm * cos) / 2
  return { x1: cx - hw, y1: cy - hh, x2: cx + hw, y2: cy + hh }
}

/**
 * Qualitäts- und Produktionswarnungen vor dem Kauf. Läuft identisch im Editor und im
 * Render-Service (dort mit den echten Upload-Maßen); Blocker verhindern Add-to-Cart.
 */
export function computeWarnings(doc: DesignDocument, ctx: WarningsContext = {}): DesignWarning[] {
  const out: DesignWarning[] = []
  const fonts = ctx.fonts instanceof Set ? ctx.fonts : new Set(ctx.fonts ?? [])
  const minWarn = ctx.limits?.minDpiWarn ?? 150
  const minBlock = ctx.limits?.minDpiBlock ?? 72
  const unsupported = new Set((ctx.unsupportedMimes ?? ['image/heic', 'image/heif']).map((m) => m.toLowerCase()))
  const c = doc.canvas
  const W = c.widthMm
  const H = c.heightMm
  const safe = c.safeMarginMm

  for (const layer of doc.layers) {
    if (!layer.visible) continue
    const b = rotatedBounds(layer)

    if (layer.type === 'image') {
      const img = layer as ImageLayer
      if (img.source.kind === 'placeholder') {
        if (!img.constraints.optional) out.push({ code: 'EMPTY_SLOT', severity: 'block', layerId: layer.id, message: 'Bild-Slot ist noch leer.' })
        else out.push({ code: 'EMPTY_SLOT', severity: 'warn', layerId: layer.id, message: 'Optionaler Bild-Slot ist leer und wird nicht gedruckt.' })
        continue
      }
      if (img.source.kind === 'upload') {
        const info = ctx.uploads?.[img.source.uploadId]
        const status = String(info?.status ?? '').toLowerCase()
        if (info && status && status !== 'done') {
          out.push({ code: 'IMAGE_NOT_READY', severity: 'block', layerId: layer.id, message: 'Bild wird noch verarbeitet.' })
        }
        if (info?.mime && unsupported.has(info.mime.toLowerCase())) {
          out.push({ code: 'UNSUPPORTED_UPLOAD_FORMAT', severity: 'block', layerId: layer.id, message: `Dateiformat ${info.mime} kann nicht gedruckt werden.` })
        }
        const width = info?.widthPx || img.source.width || 0
        const height = info?.heightPx || img.source.height || 0
        if (width > 0 && height > 0) {
          const vis = visibleSourcePx(layer.frame, { width, height }, img.fit)
          const visibleWmm = Math.min(layer.frame.wMm, computeImagePlacement(layer.frame, { width, height }, img.fit).wMm)
          const dpi = visibleWmm > 0 ? vis.widthPx / (visibleWmm / MM_PER_INCH) : 0
          if (dpi > 0 && dpi < minBlock) {
            out.push({ code: 'LOW_DPI', severity: 'block', layerId: layer.id, message: `Bild hat nur ca. ${Math.round(dpi)} dpi (Minimum ${minBlock}).`, data: { effectiveDpi: Math.round(dpi) } })
          } else if (dpi > 0 && dpi < minWarn) {
            out.push({ code: 'LOW_DPI', severity: 'warn', layerId: layer.id, message: `Bild hat ca. ${Math.round(dpi)} dpi; für gestochen scharfen Druck werden ${minWarn}+ empfohlen.`, data: { effectiveDpi: Math.round(dpi) } })
          }
          if (img.fit.mode === 'cover') {
            const placement = computeImagePlacement(layer.frame, { width, height }, img.fit)
            if (!placementCoversFrame(layer.frame, placement)) {
              out.push({ code: 'IMAGE_NOT_COVERING', severity: 'warn', layerId: layer.id, message: 'Bild füllt seinen Rahmen nicht vollständig.' })
            }
          }
        }
      }
      continue
    }

    // Text / Sticker / Form: Inhalt im Beschnitt oder außerhalb des Sicherheitsbereichs
    const inBleed = b.x1 < 0 || b.y1 < 0 || b.x2 > W || b.y2 > H
    const outsideSafe = safe > 0 && (b.x1 < safe || b.y1 < safe || b.x2 > W - safe || b.y2 > H - safe)
    if (inBleed) {
      out.push({ code: 'CONTENT_IN_BLEED', severity: 'warn', layerId: layer.id, message: 'Element ragt in den Beschnitt und wird teilweise abgeschnitten.' })
    } else if (outsideSafe && layer.type === 'text') {
      out.push({ code: 'TEXT_OUTSIDE_SAFE', severity: 'warn', layerId: layer.id, message: 'Text liegt außerhalb des Sicherheitsbereichs und kann angeschnitten werden.' })
    }

    if (layer.type === 'text') {
      if (fonts.size > 0 && !fonts.has(layer.font.familyId)) {
        out.push({ code: 'FONT_MISSING', severity: 'block', layerId: layer.id, message: 'Schriftart ist nicht (mehr) verfügbar.' })
      }
      if (!layer.text.trim() && !layer.constraints.optional) {
        out.push({ code: 'EMPTY_TEXT', severity: 'warn', layerId: layer.id, message: 'Textfeld ist leer.' })
      }
      if (layer.autoFit === 'none' && ctx.overflowingTextLayerIds?.includes(layer.id)) {
        out.push({ code: 'TEXT_OVERFLOW', severity: 'warn', layerId: layer.id, message: 'Text passt nicht in sein Feld.' })
      }
    }
  }

  return out
}

export function hasBlockingWarnings(warnings: DesignWarning[]): boolean {
  return warnings.some((w) => w.severity === 'block')
}
